import type {
  RelationshipHistoryEntry,
  SpousePartnerEdge,
} from '@/lib/familyUnits';

export type CanonicalTreeExternalEvidenceLoadStatus =
  | 'SUCCESS'
  | 'FAILED'
  | 'UNAVAILABLE';

export type CanonicalTreeExternalEvidenceCollection =
  | 'spouse_partner_edges'
  | 'relationship_history';

export interface CanonicalTreeExternalEvidenceLoadFailure {
  readonly code:
    string | null;

  readonly message:
    string;

  readonly details:
    string | null;
}

export interface CanonicalTreeExternalEvidenceLoadResult<T> {
  readonly collection:
    CanonicalTreeExternalEvidenceCollection;

  readonly status:
    CanonicalTreeExternalEvidenceLoadStatus;

  readonly ownerUserId:
    string | null;

  readonly rows:
    readonly T[];

  readonly requestedAt:
    string;

  readonly completedAt:
    string;

  readonly authoritative:
    boolean;

  readonly failure:
    CanonicalTreeExternalEvidenceLoadFailure | null;
}

export type CanonicalTreeSpousePartnerEdgeLoadResult =
  CanonicalTreeExternalEvidenceLoadResult<SpousePartnerEdge>;

export type CanonicalTreeRelationshipHistoryLoadResult =
  CanonicalTreeExternalEvidenceLoadResult<RelationshipHistoryEntry>;

const freezeRows = <T>(
  rows:
    readonly T[],
): readonly T[] =>
  Object.freeze([
    ...rows,
  ]);

const freezeFailure = (
  failure:
    CanonicalTreeExternalEvidenceLoadFailure | null,
): CanonicalTreeExternalEvidenceLoadFailure | null =>
  failure
    ? Object.freeze({
        ...failure,
      })
    : null;

export const createSuccessfulCanonicalTreeExternalEvidenceLoadResult = <T>(
  input: {
    readonly collection:
      CanonicalTreeExternalEvidenceCollection;

    readonly ownerUserId:
      string | null;

    readonly rows:
      readonly T[];

    readonly requestedAt:
      string;

    readonly completedAt:
      string;
  },
): CanonicalTreeExternalEvidenceLoadResult<T> =>
  Object.freeze({
    collection:
      input.collection,

    status:
      'SUCCESS',

    ownerUserId:
      input.ownerUserId,

    rows:
      freezeRows(
        input.rows,
      ),

    requestedAt:
      input.requestedAt,

    completedAt:
      input.completedAt,

    authoritative:
      true,

    failure:
      null,
  });

export const createFailedCanonicalTreeExternalEvidenceLoadResult = <T>(
  input: {
    readonly collection:
      CanonicalTreeExternalEvidenceCollection;

    readonly ownerUserId:
      string | null;

    readonly requestedAt:
      string;

    readonly completedAt:
      string;

    readonly failure:
      CanonicalTreeExternalEvidenceLoadFailure;
  },
): CanonicalTreeExternalEvidenceLoadResult<T> =>
  Object.freeze({
    collection:
      input.collection,

    status:
      'FAILED',

    ownerUserId:
      input.ownerUserId,

    rows:
      Object.freeze([]),

    requestedAt:
      input.requestedAt,

    completedAt:
      input.completedAt,

    authoritative:
      false,

    failure:
      freezeFailure(
        input.failure,
      ),
  });

export const createUnavailableCanonicalTreeExternalEvidenceLoadResult = <T>(
  input: {
    readonly collection:
      CanonicalTreeExternalEvidenceCollection;

    readonly ownerUserId:
      string | null;

    readonly requestedAt:
      string;

    readonly completedAt:
      string;

    readonly failure:
      CanonicalTreeExternalEvidenceLoadFailure;
  },
): CanonicalTreeExternalEvidenceLoadResult<T> =>
  Object.freeze({
    collection:
      input.collection,

    status:
      'UNAVAILABLE',

    ownerUserId:
      input.ownerUserId,

    rows:
      Object.freeze([]),

    requestedAt:
      input.requestedAt,

    completedAt:
      input.completedAt,

    authoritative:
      false,

    failure:
      freezeFailure(
        input.failure,
      ),
  });

export const canonicalTreeExternalEvidenceLoadResultIsAuthoritative = <T>(
  result:
    CanonicalTreeExternalEvidenceLoadResult<T>,
): boolean =>
  result.status ===
    'SUCCESS' &&
  result.authoritative &&
  result.failure ===
    null;

export const canonicalTreeExternalEvidenceLoadResultsShareOwner = (
  input: {
    readonly spousePartnerEdges:
      CanonicalTreeSpousePartnerEdgeLoadResult;

    readonly relationshipHistory:
      CanonicalTreeRelationshipHistoryLoadResult;
  },
): boolean =>
  input.spousePartnerEdges
    .ownerUserId ===
  input.relationshipHistory
    .ownerUserId;

export const canonicalTreeExternalEvidenceLoadResultsAreSnapshotReady = (
  input: {
    readonly spousePartnerEdges:
      CanonicalTreeSpousePartnerEdgeLoadResult;

    readonly relationshipHistory:
      CanonicalTreeRelationshipHistoryLoadResult;
  },
): boolean =>
  canonicalTreeExternalEvidenceLoadResultIsAuthoritative(
    input.spousePartnerEdges,
  ) &&
  canonicalTreeExternalEvidenceLoadResultIsAuthoritative(
    input.relationshipHistory,
  ) &&
  canonicalTreeExternalEvidenceLoadResultsShareOwner(
    input,
  );
